import type {
  Cropping,
  Dpr,
  ImageWidths,
  Ratio,
} from '~/types/AppData/PartialImageTypes';
import type { Image } from '~/types/AppData/PageContent/Image';

export const createImageSrc = (
  host: string,
  path: string,
  imageWidth: number,
  aspectRatio: Ratio,
  dpr: Dpr,
  croppingData: Cropping | null
): string => {
  const isHighDprInLargeImage = imageWidth >= 600 && dpr > 2;
  const autoParams = 'f_auto,q_auto,g_auto';

  if (isHighDprInLargeImage) {
    return createImageSrc(host, path, imageWidth, aspectRatio, 2, croppingData);
  }

  if (aspectRatio.original === true) {
    return `${host}${autoParams}/${path}`;
  }

  const croppingConfig = croppingData
    ? `c_crop,x_${croppingData.x},y_${croppingData.y},w_${croppingData.width},h_${croppingData.height}/`
    : '';
  return `${host}${croppingConfig}c_fill,w_${imageWidth * dpr},ar_${aspectRatio.x}:${aspectRatio.y},${autoParams}/${path}`;
};

export const getImageSrcAndSrcSet = (
  imageWidths: ImageWidths,
  host: string,
  path: string,
  croppingData?: Cropping,
  imageAspectRatios?: string | { x: number; y: number }
) => {
  const encodedPathForSrcset = host.includes('minutemediacdn')
    ? encodeURIComponent(path)
    : path;
  const aspectRatio = createAspectRatio(imageAspectRatios);
  const cropping = (cropping: any) => {
    if (!cropping) return null;

    return {
      x: cropping?.x || 0,
      y: cropping?.y || 0,
      width: cropping?.width || 0,
      height: cropping?.height || 0,
    };
  };

  return {
    smallSrc: `${createImageSrc(host, encodedPathForSrcset, imageWidths.small, aspectRatio, 1, cropping(croppingData))} 1x, 
    ${createImageSrc(host, encodedPathForSrcset, imageWidths.small, aspectRatio, 2, cropping(croppingData))} 2x, 
    ${createImageSrc(host, encodedPathForSrcset, imageWidths.small, aspectRatio, 3, cropping(croppingData))} 3x`,
    mediumSrc: `${createImageSrc(host, encodedPathForSrcset, imageWidths.medium, aspectRatio, 1, cropping(croppingData))} 1x,
    ${createImageSrc(host, encodedPathForSrcset, imageWidths.medium, aspectRatio, 2, cropping(croppingData))} 2x, 
    ${createImageSrc(host, encodedPathForSrcset, imageWidths.medium, aspectRatio, 3, cropping(croppingData))} 3x`,
    largeSrc: `${createImageSrc(host, encodedPathForSrcset, imageWidths.large, aspectRatio, 1, cropping(croppingData))} 1x,
    ${createImageSrc(host, encodedPathForSrcset, imageWidths.large, aspectRatio, 2, cropping(croppingData))} 2x,
    ${createImageSrc(host, encodedPathForSrcset, imageWidths.large, aspectRatio, 3, cropping(croppingData))} 3x`,

    lowResSrc: createImageSrc(
      host,
      path,
      16,
      { x: 16, y: 9 },
      1,
      cropping(croppingData)
    ),

    fallbackSrc: createImageSrc(
      host,
      path,
      imageWidths.medium,
      aspectRatio,
      1,
      cropping(croppingData)
    ),
  };
};

export const createAspectRatio = (
  aspectRatio?: string | { x: number; y: number }
): Ratio => {
  let x, y;

  if (typeof aspectRatio === 'object') {
    x = aspectRatio.x;
    y = aspectRatio.y;
  }

  if (typeof aspectRatio === 'string') {
    const widthAndHeight = aspectRatio.split('/');
    x = parseInt(widthAndHeight[0], 10);
    y = parseInt(widthAndHeight[1], 10);
  }

  return {
    x: x || 16,
    y: y || 9,
    original: aspectRatio === 'original',
  };
};

const formatCredit = (credit: string = '') => {
  return credit
    ? credit.replace(/<div\b[^>]*>/g, '<span>').replace(/<\/div>/g, '</span>')
    : '';
};

export const getImageCaption = (credit?: string, caption?: string): string => {
  return [caption, formatCredit(credit)]
    .filter((value) => {
      return typeof value === 'string' && value.trim() !== '';
    })
    .join(' | ');
};

export const get169ImageSrc = (
  image: Image | undefined,
  fallbackHost: string = ''
) => {
  if (!image) return '';

  let imageSrc = createImageSrc(
    image.host ?? fallbackHost,
    image.path,
    1440,
    { x: 1440, y: 810 },
    1,
    image.cropping ?? null
  );

  if (image.fileExtension) {
    imageSrc += `.${image.fileExtension}`;
  }

  return imageSrc;
};

export const replaceCloudinaryUrlHost = (url: string) => {
  return url.replace(
    'https://minutemedia-ressh.cloudinary.com',
    'https://images2.minutemediacdn.com'
  );
};
